/**
 * SlideWithImage - Wrapper that adds lazy image loading to slides
 * 
 * Wraps SlideRenderer and uses useSlideImage hook to fetch images
 * when the slide becomes visible (current slide).
 */

import { useMemo, useEffect, useRef } from 'react';
import { SlideRenderer } from './SlideRenderer';
import { useSlideImage } from '@/hooks/useSlideImage';
import type { SlideContent } from '../../services/mockSlideDeckService';

interface SlideWithImageProps {
    slide: SlideContent;
    isVisible: boolean; // Is this the currently viewed slide?
    journalId?: string;
    compact?: boolean;
    onImageResolved?: (slideId: string, imageUrl: string) => void; // Callback when image is resolved
}

export const SlideWithImage = ({ slide, isVisible, journalId, compact = false, onImageResolved }: SlideWithImageProps) => {
    // Extract image info from slide
    const imagePrompt = slide.content?.image?.prompt || '';
    const existingUrl = slide.content?.image?.url || '';

    // Use lazy image loading hook
    const { imageUrl, isLoading } = useSlideImage({
        slideId: slide.id,
        imagePrompt,
        existingUrl,
        isVisible,
        journalId,
    });

    // Track if we've already notified parent about this image
    const hasNotifiedRef = useRef(false);

    // Notify parent when image is resolved (for persistence)
    useEffect(() => {
        if (imageUrl && imageUrl !== existingUrl && !hasNotifiedRef.current) {
            hasNotifiedRef.current = true;
            console.log(`💾 Persisting resolved image for slide ${slide.id}`);
            onImageResolved?.(slide.id, imageUrl);
        }
    }, [imageUrl, existingUrl, slide.id, onImageResolved]);

    // Reset notification flag when slide changes
    useEffect(() => {
        hasNotifiedRef.current = false;
    }, [slide.id]);

    // Create updated slide with resolved image
    const slideWithImage = useMemo(() => {
        // If no image or already has URL, return original
        if (!slide.content?.image || existingUrl) {
            return slide;
        }

        // If we got a new image URL, update the slide
        if (imageUrl) {
            return {
                ...slide,
                content: {
                    ...slide.content,
                    image: {
                        ...slide.content.image,
                        url: imageUrl,
                    },
                },
            };
        }

        // If loading, add loading indicator to image
        if (isLoading) {
            return {
                ...slide,
                content: {
                    ...slide.content,
                    image: {
                        ...slide.content.image,
                        url: '', // Empty URL shows loading state
                        alt: 'Loading image...',
                    },
                },
            };
        }

        return slide;
    }, [slide, imageUrl, isLoading, existingUrl]);

    return <SlideRenderer slide={slideWithImage} compact={compact} />;
};
